/**
 * Pages-native subform row helpers (no window / layout dependency).
 * Rows are plain objects stored on formPayload[name].selected_value.
 */

export const createEmptyRow = (columnNames = [], extras = {}) => {
  const row = {
    is_selected: false,
    isOpenBox: false,
    _scmRowId: `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  };
  (columnNames || []).forEach((key) => {
    if (key) {
      row[key] = "";
    }
  });
  return { ...row, ...extras };
};

export const ensureRowId = (row = {}) => {
  if (row && row._scmRowId) {
    return row;
  }
  return {
    ...row,
    _scmRowId: `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  };
};

/**
 * CREATE/clone must not send persisted metatable row ids — Save API treats any
 * `id` as an existing subform row and returns "Subform row id does not exist".
 * Keeps `_scmRowId` and lookup fields (cf_packages).
 */
export const stripPersistedSubformRowIds = (rows) => {
  if (!Array.isArray(rows)) {
    return rows;
  }
  return rows.map((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return row;
    }
    if (row.id == null || row.id === "") {
      return row;
    }
    const next = { ...row };
    delete next.id;
    return next;
  });
};

const DISPLAY_POSITION_KEYS = [
  "subform_link_display_position",
  "display_position",
  "displayPosition",
  "displayPostition",
  "cf_display_position",
  "subform_field_display_position",
  "line_number",
  "display_index",
  "sequence",
];

export const resolveSubformRowDisplayPosition = (row, fallbackIndex = 0) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return fallbackIndex;
  }
  for (let i = 0; i < DISPLAY_POSITION_KEYS.length; i += 1) {
    const key = DISPLAY_POSITION_KEYS[i];
    if (!Object.prototype.hasOwnProperty.call(row, key)) {
      continue;
    }
    const raw = row[key];
    if (raw == null || raw === "") {
      continue;
    }
    const num = Number(raw);
    if (Number.isFinite(num)) {
      return num;
    }
  }
  return fallbackIndex;
};

const rowHasDisplayPosition = (row) => {
  if (!row || typeof row !== "object") {
    return false;
  }
  return DISPLAY_POSITION_KEYS.some((key) => {
    if (!Object.prototype.hasOwnProperty.call(row, key)) {
      return false;
    }
    const raw = row[key];
    return raw != null && raw !== "" && Number.isFinite(Number(raw));
  });
};

/** Match layout subform row order (metatable_subform.display_position). */
export const sortPagesSubformRowsLikeLayout = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) {
    return rows;
  }
  if (!rows.some(rowHasDisplayPosition)) {
    return rows;
  }
  return rows
    .map((row, index) => ({
      row,
      index,
      pos: resolveSubformRowDisplayPosition(row, index),
    }))
    .sort((a, b) => a.pos - b.pos || a.index - b.index)
    .map(({ row }) => row);
};

const packageWeightRaw = (row) =>
  row?.weight ?? row?.Weight ?? row?.cf_weight ?? row?.cf_Weight;

const unwrapPicklistScalar = (raw) => {
  if (raw == null || Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    if (raw.value != null && raw.value !== "") return raw.value;
    if (raw.name != null && raw.name !== "") return raw.name;
    if (raw.label != null && raw.label !== "") return raw.label;
    return raw;
  }
  return raw;
};

const firstNonEmpty = (obj, keys) => {
  if (!obj || typeof obj !== "object") return null;
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (v != null && v !== "") return v;
  }
  return null;
};

const toFixed2OrRaw = (val) => {
  const n = Number(val);
  // Keep unset package dims as empty string — "0.00" looks blank in UI (placeholder)
  // but LENGTH("0.00")>=1 so save-shipment skips weight/length/width/height_error.
  if (Number.isFinite(n) && n === 0) {
    return "";
  }
  return Number.isFinite(n) ? n.toFixed(2) : val;
};

/**
 * Step/search-by-reference package rows use window api names (cf_weight, nested cf_packages).
 * Add Shipment subform columns expect weight/length/… and cf_packages as { label, value, id }.
 */
export const normalizePagesSubformPackageRow = (row) => {
  if (row == null || typeof row !== "object" || Array.isArray(row)) {
    return row;
  }
  const next = { ...row };

  const copyScalar = (target, ...sources) => {
    if (next[target] != null && next[target] !== "") return;
    const raw = firstNonEmpty(row, sources);
    if (raw == null || raw === "") return;
    next[target] = unwrapPicklistScalar(raw);
  };

  copyScalar("weight", "weight", "Weight", "cf_weight", "cf_Weight");
  copyScalar("length", "length", "Length", "cf_length", "cf_Length");
  copyScalar("width", "width", "Width", "cf_width", "cf_Width");
  copyScalar("height", "height", "Height", "cf_height", "cf_Height");
  copyScalar(
    "description",
    "description",
    "cf_package_description_(allow_only_255_characters)",
    "cf_description"
  );
  // Subform Description column binds to cf_description (window api name).
  if (
    (next.cf_description == null || next.cf_description === "") &&
    next.description != null &&
    next.description !== ""
  ) {
    next.cf_description = next.description;
  }

  if (next.uom == null || next.uom === "") {
    const uomRaw =
      firstNonEmpty(row, ["uom", "UOM", "cf_unit_of_measure", "cf_uom"]) ?? "LBS";
    next.uom = unwrapPicklistScalar(uomRaw);
  } else {
    next.uom = unwrapPicklistScalar(next.uom);
  }

  ["weight", "length", "width", "height"].forEach((key) => {
    if (next[key] != null && next[key] !== "") {
      next[key] = toFixed2OrRaw(next[key]);
    }
  });

  let pkg = next.cf_packages;
  if (pkg != null && typeof pkg === "object" && !Array.isArray(pkg)) {
    if ("value" in pkg || "label" in pkg || "text" in pkg) {
      const value =
        pkg.value != null && pkg.value !== ""
          ? String(pkg.value)
          : pkg.record_id != null
          ? String(pkg.record_id)
          : "";
      const label =
        pkg.label ??
        pkg.text ??
        firstNonEmpty(pkg, ["cf_package_name", "package_name", "name"]) ??
        firstNonEmpty(row, ["cf_package_name", "package_label", "package_name"]) ??
        "";
      if (value) {
        next.cf_packages = {
          id: pkg.id != null && pkg.id !== "" ? String(pkg.id) : value,
          label: String(label),
          value,
        };
      }
    } else {
      const recordId =
        firstNonEmpty(pkg, ["record_id", "recordId", "value"]) ??
        firstNonEmpty(row, ["record_id", "recordId"]);
      const label =
        firstNonEmpty(pkg, ["cf_package_name", "package_name", "name"]) ??
        firstNonEmpty(row, ["cf_package_name", "package_label", "package_name"]) ??
        "";
      const rowId = pkg.id != null && pkg.id !== "" ? String(pkg.id) : "";
      if (recordId) {
        next.cf_packages = {
          id: rowId || String(recordId),
          label: String(label),
          value: String(recordId),
        };
      }
    }
  } else if (
    (pkg == null || pkg === "") &&
    firstNonEmpty(row, ["cf_package_name", "package_label", "package_name"])
  ) {
    const recordId = firstNonEmpty(row, ["record_id", "recordId"]);
    const label = firstNonEmpty(row, [
      "cf_package_name",
      "package_label",
      "package_name",
    ]);
    if (recordId) {
      next.cf_packages = {
        id: row.id != null ? String(row.id) : String(recordId),
        label: String(label),
        value: String(recordId),
      };
    }
  }

  return next;
};

export const normalizePagesSubformPackageRows = (rows) => {
  if (!Array.isArray(rows)) {
    return rows;
  }
  return rows.map((row) => normalizePagesSubformPackageRow(row)).filter(Boolean);
};

/** Drop blank package rows (initialrow / partial step output) before subform render. */
export const filterEmptyPagesPackageSubformRows = (rows) => {
  if (!Array.isArray(rows)) {
    return rows;
  }
  return rows.filter((row) => {
    if (row == null || typeof row !== "object") {
      return false;
    }
    if (Object.keys(row).length === 0) {
      return false;
    }
    const weightNum = Number(packageWeightRaw(row));
    if (Number.isFinite(weightNum) && weightNum > 0) {
      return true;
    }
    const dimKeys = ["length", "Length", "width", "Width", "height", "Height"];
    return dimKeys.some((key) => {
      const n = Number(row[key]);
      return Number.isFinite(n) && n > 0;
    });
  });
};

export const normalizeRows = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set();
  return value.map((row, index) => {
    const base =
      row && typeof row === "object" && !Array.isArray(row)
        ? ensureRowId({
            is_selected: row.is_selected === true,
            isOpenBox: row.isOpenBox === true,
            ...row,
          })
        : ensureRowId({ is_selected: false, isOpenBox: false, value: row });
    if (!seen.has(base._scmRowId)) {
      seen.add(base._scmRowId);
      return base;
    }
    // Copy used to keep the source id. A stable suffix repairs those rows
    // without minting a new random id on every read.
    let suffix = index;
    let uniqueId = `${base._scmRowId}__${suffix}`;
    while (seen.has(uniqueId)) {
      suffix += 1;
      uniqueId = `${base._scmRowId}__${suffix}`;
    }
    seen.add(uniqueId);
    return { ...base, _scmRowId: uniqueId };
  });
};

export const readRowsFromFormPayload = (fieldFormPayload, name) => {
  if (!name || !fieldFormPayload || typeof fieldFormPayload !== "object") {
    return [];
  }
  // HTML name="Package" vs pages api_name "package" — resolve either key.
  let entry = fieldFormPayload[name];
  if (entry == null) {
    const lower = String(name).toLowerCase();
    const matchedKey = Object.keys(fieldFormPayload).find(
      (key) => String(key).toLowerCase() === lower
    );
    if (matchedKey) {
      entry = fieldFormPayload[matchedKey];
    }
  }
  let rawSelected = entry?.selected_value;
  // Get Rates RATINGS stringify Package for HyperThread; remount must parse back
  // or normalizeRows([]) → initialrow blank → D&R loses the multi-row dims.
  if (typeof rawSelected === "string") {
    const trimmed = rawSelected.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          rawSelected = parsed;
        }
      } catch (e) {
        // keep string; normalizeRows yields []
      }
    }
  }
  let rows = normalizeRows(rawSelected);
  if (String(name).toLowerCase() === "package" && Array.isArray(rows)) {
    rows = normalizePagesSubformPackageRows(rows);
    const withoutEmpty = filterEmptyPagesPackageSubformRows(rows);
    rows = withoutEmpty.length > 0 ? withoutEmpty : rows;
    rows = sortPagesSubformRowsLikeLayout(rows);
  }
  return rows;
};

export const addRow = (rows, columnNames = [], extras = {}) => {
  return [...normalizeRows(rows), createEmptyRow(columnNames, extras)];
};

/** Add a row already in edit mode (detail view). Optionally close other open rows. */
export const addRowInEdit = (rows, columnNames = [], { exclusive = true } = {}) => {
  const list = normalizeRows(rows).map((row) =>
    exclusive ? { ...row, isOpenBox: false } : row
  );
  return [
    ...list,
    createEmptyRow(columnNames, { isOpenBox: true, _isNew: true }),
  ];
};

export const beginRowEdit = (rows, rowIndex, { exclusive = true } = {}) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length) {
    return list;
  }
  return list.map((row, i) => ({
    ...row,
    isOpenBox: exclusive ? i === rowIndex : i === rowIndex ? true : row.isOpenBox,
  }));
};

export const endRowEdit = (rows, rowIndex) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length) {
    return list;
  }
  const next = [...list];
  const cleaned = { ...next[rowIndex], isOpenBox: false };
  delete cleaned._isNew;
  next[rowIndex] = cleaned;
  return next;
};

/**
 * Inline save one row: keep that row's current values, revert every other
 * open row to its snapshot (or drop unsaved new rows), close all edits.
 */
export const saveOneRowRevertOthers = (rows, saveIndex, snapshotsById = {}) => {
  const list = normalizeRows(rows);
  if (saveIndex < 0 || saveIndex >= list.length) {
    return list;
  }
  const next = [];
  list.forEach((row, i) => {
    if (i === saveIndex) {
      const cleaned = { ...row, isOpenBox: false };
      delete cleaned._isNew;
      next.push(cleaned);
      return;
    }
    if (row.isOpenBox === true) {
      if (row._isNew) {
        return; // discard other unsaved new rows
      }
      const snap = snapshotsById[row._scmRowId];
      if (snap) {
        const restored = { ...snap, isOpenBox: false, _scmRowId: row._scmRowId };
        delete restored._isNew;
        next.push(restored);
        return;
      }
    }
    const closed = { ...row, isOpenBox: false };
    delete closed._isNew;
    next.push(closed);
  });
  return next;
};

/** Bulk save: keep current values for all rows, close all edits. */
export const saveAllOpenRows = (rows) => {
  return normalizeRows(rows).map((row) => {
    const cleaned = { ...row, isOpenBox: false };
    delete cleaned._isNew;
    return cleaned;
  });
};

export const cloneRowSnapshot = (row) => {
  try {
    return JSON.parse(JSON.stringify(row || {}));
  } catch (e) {
    return { ...(row || {}) };
  }
};

export const formatCellDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "value")) {
      return formatCellDisplayValue(value.value);
    }
    if (Object.prototype.hasOwnProperty.call(value, "dpnm")) {
      return String(value.dpnm);
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
};

export const copyRow = (rows, rowIndex) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length) {
    return list;
  }
  const cloneSource = { ...list[rowIndex], is_selected: false };
  delete cloneSource._scmRowId;
  const clone = ensureRowId(cloneSource);
  const next = [...list];
  next.splice(rowIndex + 1, 0, clone);
  return next;
};

export const deleteRow = (rows, rowIndex) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length) {
    return list;
  }
  return list.filter((_, i) => i !== rowIndex);
};

export const deleteSelectedRows = (rows) => {
  const list = normalizeRows(rows);
  const remaining = list.filter((row) => row.is_selected !== true);
  return remaining;
};

export const setRowSelected = (rows, rowIndex, selected) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length) {
    return list;
  }
  const next = [...list];
  next[rowIndex] = { ...next[rowIndex], is_selected: !!selected };
  return next;
};

export const setAllRowsSelected = (rows, selected) => {
  return normalizeRows(rows).map((row) => ({
    ...row,
    is_selected: !!selected,
  }));
};

export const updateRowField = (rows, rowIndex, fieldName, value) => {
  const list = normalizeRows(rows);
  if (rowIndex < 0 || rowIndex >= list.length || !fieldName) {
    return list;
  }
  const next = [...list];
  next[rowIndex] = {
    ...next[rowIndex],
    [fieldName]: value,
  };
  return next;
};

/** Collect scm:inputtext name attrs from markup children (default-mode columns). */
export const collectColumnNamesFromChildren = (children = []) => {
  return collectFieldNodesFromChildren(children).map((node) => node.attribs.name);
};

/**
 * Leaf scm:inputtext nodes in document order (unique by name).
 * Used by layout="columns" (field labels down the left; each package is a column).
 */
export const collectFieldNodesFromChildren = (children = []) => {
  const nodes = [];
  const seen = new Set();
  const walk = (list) => {
    (list || []).forEach((node) => {
      const tag = String(node?.name || "").toLowerCase();
      if (tag === "scm:inputtext") {
        const n = node?.attribs?.name;
        if (n && !String(n).includes(".") && !seen.has(String(n).toLowerCase())) {
          seen.add(String(n).toLowerCase());
          nodes.push(node);
        }
        return;
      }
      if (node?.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(children);
  return nodes;
};

export const rowsForPayload = (rows) => {
  return normalizeRows(rows).map((row) => {
    const { is_selected, ...rest } = row;
    return rest;
  });
};
